import { useCallback, useState } from 'react'
import { Upload, Folder, X } from 'lucide-react'
import { usePresignedUpload, UploadInProgressError } from '../lib/hooks'
import { filterImageFiles } from '../lib/image-files'
import { folderLogger } from '../lib/logger'
import NotificationModal from './ui/notification-modal'
import { memoirUploadRefusal } from '../lib/memoir-errors'

interface UploadInterfaceProps {
  /** The Memoir the uploaded album is created in (SOL-204). Required by the server. */
  memoirId: string
  onUploadComplete?: () => void
  className?: string
}

interface NotificationState {
  isOpen: boolean
  title: string
  message: string
  variant: 'success' | 'error' | 'info'
}

// Derive an album name from the uploaded files: the first folder segment
// when a folder was dropped/selected, otherwise the first filename minus
// its extension.
function deriveAlbumName(files: File[]): string {
  const firstFile = files[0]
  const relativePath = (firstFile as File & { webkitRelativePath?: string }).webkitRelativePath
  if (relativePath) {
    return relativePath.split('/')[0]
  }
  return firstFile.name.replace(/\.[^/.]+$/, '') || 'New Album'
}

export default function UploadInterface({ memoirId, onUploadComplete, className = '' }: UploadInterfaceProps): JSX.Element {
  const [isDragOver, setIsDragOver] = useState(false)
  const [showUploadArea, setShowUploadArea] = useState(false)
  const { progress, uploadFiles, resetProgress } = usePresignedUpload()

  const isBusy = progress.status !== 'idle' && progress.status !== 'done' && progress.status !== 'error'

  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  })

  const showNotification = (variant: 'success' | 'error' | 'info', title: string, message: string) => {
    setNotification({ isOpen: true, variant, title, message })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  // Closing the upload area clears any stale progress so a finished (or
  // failed) progress bar doesn't reappear the next time it's opened.
  //
  // The X button is disabled while isBusy (see JSX below), so this only
  // ever runs when idle/done/error - never while an upload's network calls
  // are actually in flight. That's a deliberate interim choice: without
  // real cancellation (AbortController - out of scope here), letting the
  // user close mid-upload would leave an upload silently consuming
  // bandwidth in the background with no way to tell. Blocking the close
  // is simpler than building "still uploading in the background" UI for a
  // panel that's meant to be closed once its one job is done.
  const closeUploadArea = useCallback(() => {
    setShowUploadArea(false)
    resetProgress()
  }, [resetProgress])

  const runUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) {
      folderLogger.warn('No files to upload')
      return
    }

    const imageFiles = filterImageFiles(files)

    if (imageFiles.length === 0) {
      showNotification('error', 'No Photos Found', 'Please select photo files to upload.')
      return
    }

    const albumName = deriveAlbumName(imageFiles)

    try {
      folderLogger.info('Starting album upload', { albumName, fileCount: imageFiles.length })

      const result = await uploadFiles(albumName, imageFiles, { memoirId })

      folderLogger.info('Album upload completed', { albumUuid: result.album.uuid })
      showNotification('success', 'Album Uploaded!', `"${albumName}" has been uploaded successfully.`)

      folderLogger.debug('Calling upload complete callback')
      onUploadComplete?.()
      closeUploadArea()
    } catch (err) {
      if (err instanceof UploadInProgressError) {
        // Another run is still active - this one never started, so there's
        // nothing to reset or close. Say so plainly rather than showing a
        // generic "Upload Failed".
        showNotification('info', 'Upload In Progress', err.message)
        return
      }
      const refusal = memoirUploadRefusal(err)
      if (refusal) {
        showNotification('error', refusal.title, refusal.message)
        return
      }
      folderLogger.error('Upload failed', { error: err })
      showNotification('error', 'Upload Failed', err instanceof Error ? err.message : 'Unknown error occurred')
    }
  }, [uploadFiles, onUploadComplete, closeUploadArea, memoirId])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)

    folderLogger.info('Files dropped for upload', {
      fileCount: files.length,
      fileNames: files.map(f => f.name)
    })

    await runUpload(files)
  }, [runUpload])

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    // Reset so selecting the same file again still fires onChange
    e.target.value = ''
    await runUpload(files)
  }, [runUpload])

  if (!showUploadArea) {
    return (
      <>
        <button
          className={`upload-trigger-button ${className}`}
          onClick={() => setShowUploadArea(true)}
          disabled={isBusy}
        >
          <Upload size={16} />
          <span>Upload Album</span>
        </button>

        <NotificationModal
          isOpen={notification.isOpen}
          title={notification.title}
          message={notification.message}
          variant={notification.variant}
          onClose={closeNotification}
        />
      </>
    )
  }

  return (
    <>
      <div className="upload-interface">
        <div className="upload-header">
          <h3>Upload New Album</h3>
          <button
            className="upload-close-button"
            onClick={closeUploadArea}
            disabled={isBusy}
            title={isBusy ? 'Please wait for the upload to finish' : undefined}
          >
            <X size={16} />
          </button>
        </div>

        <div
          className={`upload-drop-area ${isDragOver ? 'upload-drop-area--active' : ''} ${isBusy ? 'upload-drop-area--loading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isBusy ? (
            <div className="upload-loading">
              <Upload size={32} />
              <p>
                {progress.status === 'initializing' && 'Preparing...'}
                {progress.status === 'uploading' && `Uploading ${progress.completedFiles}/${progress.totalFiles}...`}
                {progress.status === 'completing' && 'Finishing...'}
              </p>
              {progress.status === 'uploading' && (
                <div className="upload-progress">
                  <div className="upload-progress-bar" style={{ width: `${progress.overallProgress}%` }} />
                </div>
              )}
            </div>
          ) : (
            <>
              <Folder size={48} />
              <h4>Drag a folder here</h4>
              <p>Or click to select files</p>
              <input
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="upload-file-input"
                disabled={isBusy}
              />
            </>
          )}
        </div>

        {progress.status === 'error' && progress.error && (
          <div className="upload-error">
            <p>Error: {progress.error}</p>
          </div>
        )}
      </div>

      <NotificationModal
        isOpen={notification.isOpen}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        onClose={closeNotification}
      />
    </>
  )
}
